import { QuotationFormSchema } from "@factory/form/form.rule";
import { currencyFormat } from "@shared/utility/formats/currency-format";
import { useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";

import PreviewInputControl from "./preview-input-control";

interface SummaryTableProps {
  formData: QuotationFormSchema;
  downloading?: boolean;
  isInvoice?: boolean;
}
export function SummaryTable({ formData, downloading, isInvoice }: SummaryTableProps) {
  const [showDN, setShowDN] = useState(true)
  const { watch, control, getValues, setValue } = useFormContext<QuotationFormSchema>();
  const hideTotal = getValues("hideTotal")
  const {
    fields: customRows,
    append,
    remove,
  } = useFieldArray({
    control,
    name: "customRows",
  });

  return (
    <table className="w-full text-[0.8em] border border-black">
      <thead>
        <tr className="bg-gray-900 text-white">
          <th className="px-4 py-1 text-left font-bold">SERVICES</th>
          <th className="px-4 py-1 text-center font-bold">RATE PER PERSON</th>
          <th className="px-4 py-1 text-center font-bold">NO. OF PAX</th>
          <th className="px-4 py-1 text-center font-bold">TOTAL PRICE</th>
        </tr>
      </thead>
      <tbody>
        <tr className="bg-white border border-gray-400">
          <td colSpan={4} className="px-4 py-2 font-semibold">
            <div className="flex justify-between">
              <div>
                <span className={!showDN ? 'hidden' : 'mr-1'}>{`${formData?.numDays}D${formData?.numNights}N`}</span>
                <span>
                  {formData?.packageName}
                </span>
              </div> 
              {!downloading && (
                <button
                  className="bg-gray-200 text-gray-600 hover:bg-gray-300 px-2 ml-2"
                  onClick={() => setShowDN(!showDN)}   
                >
                {!showDN ? ("Show DN") : ("Hide DN")}
                </button>
              )}
            </div> 
          </td>
        </tr>
        <tr className="bg-white">
          <td className="border border-gray-400 px-4 py-1">ADULT RATE {formData?.numChildren !== undefined && formData?.numChildren > 0 && "(Includes child transport and accommodation)"}</td>
          <td className="border border-gray-400 px-4 py-1 text-center">
            {currencyFormat(
              (formData?.adultRateTotal || 0) / (formData.numAdults || 1) || 0
            )}
          </td>
          <td className="border border-gray-400 px-4 py-1 text-center font-semibold">
            {formData?.numAdults || 0}
          </td>
          <td className="border border-gray-400 px-4 py-1 text-center">
            {currencyFormat(formData?.adultRateTotal || 0)}
          </td>
        </tr>
        {formData?.numChildren !== undefined && formData?.numChildren > 0 && (
        <tr className="bg-white">
          <td className="border border-gray-400 px-4 py-1">CHILD RATE</td>
          <td className="border border-gray-400 px-4 py-1 text-center">
            {currencyFormat(
              (formData?.childRateTotal || 0) / (formData.numChildren || 1) || 0
            )}
          </td>
          <td className="border border-gray-400 px-4 py-1 text-center font-semibold">
            {formData?.numChildren || 0}
          </td>
          <td className="border border-gray-400 px-4 py-1 text-center">
            {currencyFormat(formData?.childRateTotal || 0)}
          </td>
        </tr>
        )}
        {customRows?.map((item, index) => {
          if (item.display_location === "services") {
          return (
            <tr key={index} className="bg-white border border-gray-400"> 
              <td className="border border-gray-400 px-4 py-1 font-semibold text-center">
                {downloading ? (
                  <p className="font-normal text-black">
                    {getValues(`customRows.${index}.col_one`)}
                  </p>
                ) : (
                    <PreviewInputControl
                      control={control}
                      name={`customRows.${index}.col_one`}
                      className="text-black font-normal w-full py-1"
                      containerClassName="flex-1"
                    />
                )}
              </td>
              <td className="border border-gray-400 px-4 py-1 font-semibold text-center">
                {downloading ? (
                  <p className="font-normal text-black">
                    {getValues(`customRows.${index}.col_two`)}
                  </p>
                ) : (
                    <PreviewInputControl
                      control={control}
                      name={`customRows.${index}.col_two`}
                      className="text-black font-normal w-3/4 py-1 text-center"
                      containerClassName="flex-1"
                    />
                )}
              </td>
              <td className="border border-gray-400 px-4 py-1 font-semibold text-center">
                {downloading ? (
                  <p className="font-normal text-black">
                    {getValues(`customRows.${index}.col_three`)}
                  </p>
                ) : (
                    <PreviewInputControl
                      control={control}
                      name={`customRows.${index}.col_three`}
                      className="text-black font-normal w-3/4 py-1 text-center"
                      containerClassName="flex-1"
                    />
                )}
              </td>
              <td className="relative border border-gray-400 px-4 py-1 font-semibold text-center">
                {downloading ? (
                  <p className="font-normal text-black">
                    {getValues(`customRows.${index}.col_four`)}
                  </p>
                ) : (
                  <div>
                    <PreviewInputControl
                      control={control}
                      name={`customRows.${index}.col_four`}
                      className="text-black font-normal w-3/4 py-1 text-center"
                      containerClassName="flex-1"
                    />
                    {!isInvoice && (
                      <button
                        onClick={() => remove(index)}
                        className="absolute -right-3 bg-red-500 text-white hover:bg-gray-600 p-1 pi pi-times rounded-full"
                      ></button>
                    )}
                  </div>
                )}
              </td>
            </tr>
          )
          }
        })}
        {!downloading && !isInvoice && (
        <tr className="bg-white border border-gray-400">
          <td colSpan={4} className="px-4 py-2 font-semibold">
              <div className="flex gap-1">
                <button
                  className="flex-1 text-gray-600 w-full py-2 hover:bg-gray-100 border border-gray-200 pi pi-plus"
                  onClick={() => append({display_location: "services" , value: "", col_one: "", col_two: "", col_three: "", col_four: ""})}
                >
                </button>
                <button
                  className="bg-gray-200 text-gray-600 w-1/5 py-2 hover:bg-gray-300"
                  onClick={() => setValue("hideTotal", !hideTotal)}
                >
                 {!hideTotal ? "Hide Total" : "Show Total"}
                </button>
              </div>
          </td>
        </tr>
        )}
        {!hideTotal && (
          <tr className="text-white border border-gray-400 ">
            <td className="px-4 py-1"></td>
            <td className="bg-blue-600 border border-gray-400 px-4 py-1 font-bold">
              GRAND TOTAL
            </td>
            <td className="bg-blue-600 border border-gray-400 px-4 py-1"></td>
            <td className="bg-blue-600 border border-gray-400 px-4 py-1 text-center font-bold">
              {currencyFormat(formData?.amount || 0)}
            </td>
          </tr>
        )}
        {customRows?.map((item, index) => {
          if (item.display_location === "services_two") {
          return (
            <tr key={index} className="bg-white border border-gray-400"> 
              <td colSpan={4} className="relative border border-gray-400 px-4 py-1 font-semibold text-center">
                {downloading ? (
                  <p className="font-normal text-black">
                    {getValues(`customRows.${index}.value`)}
                  </p>
                ) : (
                  <div> 
                    <PreviewInputControl
                      control={control}
                      name={`customRows.${index}.value`}
                      className="text-black font-normal w-full py-1"
                      containerClassName="flex-1"
                    />
                    {!isInvoice && (
                      <button
                        onClick={() => remove(index)}
                        className="absolute -right-3 -top-3 bg-red-500 text-white hover:bg-red-600 p-1 pi pi-times rounded-full"
                      ></button>
                    )}
                  </div>
                )}
              </td>
            </tr>
          )
          }
        })}
        {!downloading && !isInvoice && (
        <tr className="bg-white border border-gray-400">
          <td colSpan={4} className="px-4 py-2 font-semibold">
              <div className="flex gap-1">
                <button
                  className="flex-1 text-gray-600 w-full py-2 hover:bg-gray-100 border border-gray-200 pi pi-plus"
                  onClick={() => append({display_location: "services_two" , value: "", col_one: "", col_two: "", col_three: "", col_four: ""})}
                >
                </button>
              </div>
          </td>
        </tr>
        )}
      </tbody>
    </table>
  );
}

export default SummaryTable;
